// =============================================================================
// VandieZ admin-web -- Roles list (/roles)
// =============================================================================
// Two-column layout: every role on the left with its permission count;
// click a row to land on /roles/:roleId to edit.  "New role" opens an
// inline form (system roles can't be created from the UI — they're seeded
// via migration).
// =============================================================================

import { AlertCircle, Loader2, Plus, Trash2 } from "lucide-react";
import type React from "react";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  masterdataAdminApi,
  permissionsAdminApi,
  rolesAdminApi,
} from "@/api/client";
import { PageHeader } from "@/components/AdminLayout";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/lib/auth";
import { ApiError } from "@/types/auth";
import type {
  Permission,
  Role,
  RoleCapabilityMatrix,
} from "@/types/rbac";

export function RolesListPage(): React.JSX.Element {
  const { accessToken } = useAuth();
  const navigate = useNavigate();

  const [roles, setRoles] = useState<Role[]>([]);
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Inline create form.
  const [showForm, setShowForm] = useState<boolean>(false);
  const [name, setName] = useState<string>("");
  const [description, setDescription] = useState<string>("");
  const [selectedPerms, setSelectedPerms] = useState<Set<string>>(new Set());
  const [creating, setCreating] = useState<boolean>(false);

  // PR-04 capability matrix — see the card at the bottom of the page.
  const [matrix, setMatrix] = useState<RoleCapabilityMatrix | null>(null);
  const [matrixLoading, setMatrixLoading] = useState<boolean>(true);
  const [matrixError, setMatrixError] = useState<string | null>(null);

  const fetchAll = useCallback(
    async (signal?: AbortSignal): Promise<void> => {
      if (!accessToken) {return;}
      setLoading(true);
      setError(null);
      try {
        const [rolesRes, permsRes] = await Promise.all([
          rolesAdminApi.list(accessToken, signal),
          permissionsAdminApi.list(accessToken, signal),
        ]);
        setRoles(rolesRes.roles);
        setPermissions(permsRes.permissions);
      } catch (e: unknown) {
        if (e instanceof ApiError) {setError(e.problem.detail);}
        else if (e instanceof Error && e.name === "AbortError") {/* unmount */}
        else {setError("Unexpected error. Try again.");}
      } finally {
        setLoading(false);
      }
    },
    [accessToken],
  );

  // Kept out of `fetchAll` and given its own state: the matrix is
  // supplementary reading, and a failure fetching it must not blank the
  // roles list that is the actual working surface of this page.
  const fetchMatrix = useCallback(
    async (signal?: AbortSignal): Promise<void> => {
      if (!accessToken) {return;}
      try {
        setMatrix(
          await masterdataAdminApi.getRoleCapabilityMatrix(
            accessToken,
            signal,
          ),
        );
        setMatrixError(null);
      } catch (e: unknown) {
        if (e instanceof Error && e.name === "AbortError") {return;}
        setMatrixError(
          e instanceof ApiError
            ? e.problem.detail
            : "Couldn't load the capability matrix.",
        );
      } finally {
        setMatrixLoading(false);
      }
    },
    [accessToken],
  );

  useEffect(() => {
    const c = new AbortController();
    void fetchAll(c.signal);
    void fetchMatrix(c.signal);
    return () => { c.abort(); };
  }, [fetchAll, fetchMatrix]);

  const togglePerm = (code: string): void => {
    setSelectedPerms((prev) => {
      const next = new Set(prev);
      if (next.has(code)) {next.delete(code);}
      else {next.add(code);}
      return next;
    });
  };

  const createRole = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (!accessToken) {return;}
    if (name.trim().length < 2) {
      setError("Name must be at least 2 characters.");
      return;
    }
    setCreating(true);
    setError(null);
    try {
      await rolesAdminApi.create(accessToken, {
        name: name.trim(),
        description: description.trim() || name.trim(),
        permission_codes: Array.from(selectedPerms),
      });
      setName("");
      setDescription("");
      setSelectedPerms(new Set());
      setShowForm(false);
      await fetchAll();
    } catch (err: unknown) {
      if (err instanceof ApiError) {setError(err.problem.detail);}
      else {setError("Create failed.");}
    } finally {
      setCreating(false);
    }
  };

  const deleteRole = async (role: Role): Promise<void> => {
    if (!accessToken) {return;}
    if (!window.confirm(`Delete role "${role.name}"? This cannot be undone.`)) {
      return;
    }
    setError(null);
    try {
      await rolesAdminApi.remove(accessToken, role.id);
      await fetchAll();
    } catch (err: unknown) {
      if (err instanceof ApiError) {setError(err.problem.detail);}
      else {setError("Delete failed.");}
    }
  };

  return (
    <div className="space-y-4">
      <PageHeader
        title="Roles"
        description="Compose RBAC roles from the seeded permission catalogue. System roles are read-only."
        actions={
          <Button onClick={() => { setShowForm((s) => !s); }}>
            <Plus className="mr-1 h-4 w-4" />
            {showForm ? "Cancel" : "New role"}
          </Button>
        }
      />
        {error !== null && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <Card>
          <CardHeader>
            <CardTitle>Role catalogue</CardTitle>
            <CardDescription>
              System roles are seeded via migration; their permission
              set is canonical.  Admin-created roles can be edited or
              removed when they're no longer in use.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {showForm && (
              <form
                onSubmit={createRole}
                className="rounded-md border p-4 mb-4 space-y-3"
              >
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs uppercase tracking-wide text-muted-foreground mb-1">
                      Name
                    </label>
                    <Input
                      value={name}
                      onChange={(e) => { setName(e.target.value); }}
                      placeholder="kyc_reviewer"
                    />
                  </div>
                  <div>
                    <label className="block text-xs uppercase tracking-wide text-muted-foreground mb-1">
                      Description
                    </label>
                    <Input
                      value={description}
                      onChange={(e) => { setDescription(e.target.value); }}
                      placeholder="Reviews KYC documents only"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-wide text-muted-foreground mb-1">
                    Permissions ({selectedPerms.size} selected)
                  </label>
                  <div className="rounded-md border p-2 max-h-64 overflow-y-auto space-y-1">
                    {permissions.map((p) => (
                      <label
                        key={p.code}
                        className="flex items-start gap-2 cursor-pointer text-sm hover:bg-muted/30 rounded px-1"
                      >
                        <input
                          type="checkbox"
                          className="mt-1"
                          checked={selectedPerms.has(p.code)}
                          onChange={() => { togglePerm(p.code); }}
                        />
                        <div className="flex-1 min-w-0">
                          <div className="font-mono text-xs">{p.code}</div>
                          <div className="text-xs text-muted-foreground">
                            {p.description}
                          </div>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
                <Button type="submit" disabled={creating}>
                  {creating ? "Creating…" : "Create role"}
                </Button>
              </form>
            )}

            {loading ? (
              <div className="flex items-center gap-2 py-8 text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading roles…
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <tr>
                    <th className="py-2 pr-4">Name</th>
                    <th className="py-2 pr-4">Description</th>
                    <th className="py-2 pr-4">Permissions</th>
                    <th className="py-2 pr-4">Kind</th>
                    <th className="py-2 pr-4 w-10"></th>
                  </tr>
                </thead>
                <tbody>
                  {roles.map((role) => (
                    <tr
                      key={role.id}
                      className="border-b last:border-b-0 hover:bg-muted/40 cursor-pointer"
                      onClick={() => { navigate(`/roles/${role.id}`); }}
                    >
                      <td className="py-3 pr-4 font-medium">{role.name}</td>
                      <td className="py-3 pr-4 text-muted-foreground">
                        {role.description}
                      </td>
                      <td className="py-3 pr-4">
                        {role.permission_codes.length}
                      </td>
                      <td className="py-3 pr-4">
                        {role.is_system ? (
                          <span className="rounded-full bg-muted px-2 py-0.5 text-xs uppercase tracking-wide text-muted-foreground">
                            system
                          </span>
                        ) : (
                          <span className="rounded-full bg-primary-subtle px-2 py-0.5 text-xs uppercase tracking-wide text-primary-subtle-foreground">
                            custom
                          </span>
                        )}
                      </td>
                      <td className="py-3 pr-2 text-right">
                        {!role.is_system && (
                          <button
                            type="button"
                            aria-label={`Delete role ${role.name}`}
                            className="text-danger hover:text-danger/80"
                            onClick={(e) => {
                              e.stopPropagation();
                              void deleteRole(role);
                            }}
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Role capability matrix</CardTitle>
            <CardDescription>
              A different axis from the roles above: these are the
              platform roles on <code>users.primary_role</code> — driver,
              shipper, agent, broker — not admin staff permissions.
              Generated by walking the API's own route table, so it
              cannot disagree with what is actually enforced. Read-only:
              the gates are the matrix.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {matrixError !== null && (
              <Alert variant="destructive" className="mb-3">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{matrixError}</AlertDescription>
              </Alert>
            )}

            {matrixLoading ? (
              <div className="flex items-center gap-2 py-6 text-muted-foreground text-sm">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading
                matrix…
              </div>
            ) : matrix === null ? null : (
              <>
                <p className="mb-3 rounded-md bg-muted px-3 py-2 text-muted-foreground text-xs">
                  {matrix.coverage.declaratively_gated_routes} of{" "}
                  {matrix.coverage.total_api_routes} API routes carry a
                  declarative gate. {matrix.coverage.note}
                </p>
                {matrix.roles.length === 0 ? (
                  <div className="py-6 text-center text-muted-foreground text-sm">
                    No declarative gates registered yet.
                  </div>
                ) : (
                  <div className="space-y-4">
                    {matrix.roles.map((entry) => (
                      <div key={entry.role}>
                        <h3 className="mb-1 font-medium text-sm">
                          {entry.role}{" "}
                          <span className="text-muted-foreground">
                            ({entry.capabilities.length})
                          </span>
                        </h3>
                        <ul className="space-y-0.5">
                          {entry.capabilities.map((c) => (
                            <li
                              key={`${c.method} ${c.path}`}
                              className="flex items-baseline gap-2 text-xs"
                            >
                              <span className="shrink-0 rounded bg-muted px-1.5 py-0.5 font-mono text-muted-foreground">
                                {c.method}
                              </span>
                              <span className="font-mono text-muted-foreground">
                                {c.path}
                              </span>
                              {c.capability !== `${c.method} ${c.path}` && (
                                <span>{c.capability}</span>
                              )}
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
    </div>
  );
}
